import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { NsKey } from 'enums/NsKey';
import { AccessMember } from 'features/access/api/personneMoraleAccessApi';

import { avatarClasses, avatarInitialsFromDisplayName } from './collaboratorAvatar';

interface MemberCardProps {
  member: AccessMember;
  /** Top-right actions kebab (DropdownButton) — none = read-only. */
  menu?: React.ReactNode;
  onView?: () => void;
}

/**
 * Card of a collaborator: initials avatar, Gérant / ★ Principal / Collaborateur
 * badges, membership date and Voir button. Card render only — the logic
 * (menus, modals) stays with the caller (CollaboratorsManager).
 */
export const CollaboratorCard: React.FC<MemberCardProps> = ({
  member,
  menu,
  onView
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);

  return (
    <div className="flex flex-col gap-4 rounded-2xl border bg-white p-5">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarClasses(
            member.userId,
            'soft'
          )}`}
        >
          {avatarInitialsFromDisplayName(member.displayName, member.email)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-gray-900">
            {member.displayName}
            {member.isPrincipal && (
              <span
                title={t('members.principalTooltip')}
                className="text-amber-400"
              >
                ★
              </span>
            )}
          </p>
          <p className="truncate text-xs text-gray-500">{member.email}</p>
        </div>
        {menu && <div className="flex-shrink-0">{menu}</div>}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {member.isOwner ? (
          <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {t('members.owner')}
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
            {t('members.collaborator')}
          </span>
        )}
        {member.isPrincipal && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            {t('members.principal')}
          </span>
        )}
        {member.isGerant && (
          <span
            title={t('members.signatoryTooltip')}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              member.phone ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
            }`}
          >
            {member.phone ? t('members.signatory') : t('members.signatoryPhoneMissing')}
          </span>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between border-t pt-3">
        <span className="text-sm text-gray-500">
          {t('members.membership')} ·{' '}
          <span className="font-medium text-gray-900">
            {new Date(member.dateAdhesion).toLocaleDateString()}
          </span>
        </span>
        {onView && (
          <Button color="light" onClick={onView}>
            <EyeIcon data-slot="icon" />
            {t('members.view')}
          </Button>
        )}
      </div>
    </div>
  );
};

interface AddMemberCardProps {
  onClick: () => void;
  label?: string;
  description?: string;
}

/** Dashed "Ajouter un collaborateur" tile that closes the card grid. */
export const AddMemberCard: React.FC<AddMemberCardProps> = ({
  onClick,
  label,
  description
}) => {
  const { t } = useTranslation(NsKey.PersonneMorale);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[180px] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-200 p-5 text-center transition-colors hover:border-primary/40 hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Plus size={20} />
      </span>
      <span className="text-sm font-semibold text-gray-900">
        {label ?? t('members.addCollaborator')}
      </span>
      <span className="max-w-[200px] text-xs text-gray-500">
        {description ?? t('members.addCollaboratorHint')}
      </span>
    </button>
  );
};
